import { useState, useEffect } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import Footer from "../components/Footer";

function getInitials(name) {
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

function estimateReadTime(sections) {
  const wordCount = sections.flatMap((s) => s.paragraphs).join(" ").split(/\s+/).length;
  return Math.max(1, Math.round(wordCount / 200));
}

export default function BlogArticleDetailPage({ slug, onBack, onNavigate }) {
  const [article,     setArticle]     = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [notFound,    setNotFound]    = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/api/blogs/${slug}`).then((r) => r.json()),
      fetch(`${process.env.REACT_APP_API_URL}/api/blogs`).then((r) => r.json()),
    ]).then(([single, all]) => {
      if (!single.success) { setNotFound(true); return; }
      setArticle(single.data);
      setAllArticles(all.data || []);
    }).catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  usePageTitle(article?.title || "Blog");

  if (loading) {
    return (
      <div className="min-h-screen bg-white-warm flex items-center justify-center">
        <svg className="animate-spin" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.4)" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-white-warm flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="font-cinzel font-bold text-blue-dark text-2xl">Article not found.</p>
          <button onClick={onBack}
            className="font-cinzel font-bold text-sm text-blue-primary underline underline-offset-4">
            ← Back to Blog
          </button>
        </div>
      </div>
    );
  }

  const readTime   = estimateReadTime(article.sections || []);
  const currentIdx = allArticles.findIndex((a) => a.slug === slug);
  const nextArticle = allArticles[currentIdx + 1] || allArticles[0] || article;

  return (
    <div className="min-h-screen bg-white-warm">

      {/* ══ HERO ══ */}
      <div className="bg-blue-dark relative overflow-hidden">
        {/* Background texture */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: "repeating-linear-gradient(120deg, rgba(255,255,255,0.025) 0px, rgba(255,255,255,0.025) 1px, transparent 1px, transparent 40px)" }} />
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 60% 80% at 0% 50%, rgba(0,45,181,0.4) 0%, transparent 65%)" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 pt-8 pb-14">
          {/* Back button */}
          <button onClick={onBack}
            className="inline-flex items-center gap-2 font-cinzel font-bold text-[0.72rem] tracking-[0.15em] uppercase text-white-pure/70 border border-white-pure/25 rounded-full px-5 py-2.5 mb-10 hover:text-white-pure hover:border-white-pure/50 transition-all duration-200">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path d="M13 7H1M6 2L1 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back To Blog
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left: meta + title */}
            <div className="space-y-5">
              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 font-constantia text-white-pure/60 text-sm">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  {article.date}
                </span>
                <span className="text-white-pure/20">·</span>
                <span className="inline-flex items-center gap-1.5 font-constantia text-white-pure/60 text-sm">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                  {readTime} min read
                </span>
                <span className="text-white-pure/20">·</span>
                <span className="inline-flex items-center gap-1.5 font-constantia text-white-pure/60 text-sm">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" />
                  </svg>
                  {article.category}
                </span>
              </div>

              {/* Title */}
              <h1 className="font-cinzel font-black text-white-pure leading-[1.15] tracking-[0.02em]"
                style={{ fontSize: "clamp(1.6rem, 4vw, 2.6rem)", textShadow: "0 2px 40px rgba(0,45,181,0.5)" }}>
                {article.title}
              </h1>

              {/* Excerpt */}
              <p className="font-constantia italic text-white-pure/65 text-base leading-relaxed max-w-lg">
                {article.excerpt}
              </p>

              {/* Publisher */}
              <p className="font-constantia text-white-pure/40 text-sm">
                Published by Paladin Professional Insurance Solutions · paladinbusinessservices.net
              </p>
            </div>

            {/* Right: article image */}
            <div className="flex justify-end">
              <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-[0_8px_48px_rgba(0,0,0,0.35)]">
                <img src={article.image} alt={article.title} className="w-full h-64 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8 items-start">

          {/* ── Main Content ── */}
          <div className="bg-white-pure rounded-3xl border border-blue-dark/8 shadow-[0_2px_24px_rgba(1,46,114,0.06)] p-8 sm:p-10 space-y-8">
            {article.sections.map((section, i) => (
              <div key={i} className="space-y-4">
                <h2 className="font-cinzel font-bold italic text-blue-dark text-lg leading-snug">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, j) => (
                  <p key={j} className="font-constantia text-blue-dark/75 text-[0.95rem] leading-[1.85] text-justify">
                    {para}
                  </p>
                ))}
                {i < article.sections.length - 1 && (
                  <div className="h-px bg-blue-dark/8 mt-6" />
                )}
              </div>
            ))}

            {/* Author block */}
            <div className="pt-6 border-t border-blue-dark/10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-dark flex items-center justify-center flex-shrink-0">
                <span className="font-cinzel font-bold text-white-pure text-sm">{getInitials(article.author)}</span>
              </div>
              <div>
                <p className="font-cinzel font-bold text-blue-dark text-sm">{article.author}</p>
                <p className="font-constantia text-blue-dark/50 text-sm">{article.role}</p>
              </div>
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div className="space-y-4 lg:sticky lg:top-6">
            {/* Article Tags */}
            <div className="bg-blue-dark rounded-2xl p-6 space-y-4">
              <h3 className="font-cinzel font-bold text-white-pure/60 text-[0.6rem] tracking-[0.25em] uppercase">
                Article Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag}
                    className="font-cinzel font-bold text-[0.65rem] tracking-[0.12em] uppercase text-white-pure bg-white-pure/15 border border-white-pure/20 rounded-full px-3 py-1">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Next Read */}
            <div className="bg-white-pure rounded-2xl border border-blue-dark/10 shadow-[0_2px_16px_rgba(1,46,114,0.07)] p-6 space-y-4">
              <h3 className="font-cinzel font-bold text-blue-primary text-[0.6rem] tracking-[0.25em] uppercase">
                Next Read
              </h3>
              <h4
                className="font-cinzel font-bold italic text-blue-dark text-sm leading-snug cursor-pointer hover:text-blue-primary transition-colors duration-200"
                onClick={() => onNavigate(nextArticle.slug)}
              >
                {nextArticle.title}
              </h4>
              <button
                onClick={() => onNavigate(nextArticle.slug)}
                className="font-cinzel font-bold text-[0.65rem] tracking-[0.15em] uppercase text-white-pure bg-blue-dark rounded-full px-5 py-2.5 hover:bg-blue-primary transition-colors duration-200"
              >
                Read Next
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}